/**
 * Risk Metrics Tab Component
 * Recovery mini stats, key metrics table, drawdown chart, and equity/peak path.
 */

import React, { useMemo } from "react";
import {
  Box,
  useColorModeValue,
  VStack,
  SimpleGrid,
} from "@chakra-ui/react";
import { type ColumnDef } from "@tanstack/react-table";
import { Card } from "@/components/ui/card";
import { TradeStatisticsFilters, type RiskMetricsResponse } from "../../../../types/trade";
import { useRiskMetrics } from "../../../../hooks/useTradeStatistics";
import { useStatisticsWidgetErrorGate } from "../../../../hooks/useStatisticsWidgetErrorGate";
import { StatisticsInlineError } from "../../../../components/errors/StatisticsInlineError";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { StatisticsCardHeadline, STATS_MUTED_TEXT_CLASS } from "./statisticsCardHeadline";
import {
  STATISTICS_NO_DATA_TIMEFRAME_MESSAGE,
  StatisticsDefinitionTooltip,
} from "./statisticsChartTooltip";
import { StatisticsTabEmptyState, isStatisticsFetchPending, StatisticsTabLoadingState } from "./statisticsEmptyState";
import {
  DashboardWidgetLoadingCard,
} from "../../Dashboard/components/DashboardWidgetLoadingCard";
import { riskMetricsSectionToCardType } from "../../Dashboard/components/dashboardWidgetLoadingMessages";
import { riskMetricsHasData } from "./riskMetricsUtils";
import { statisticsShadcnCardClassName } from "./statisticsShadcnCardShell";
import { cn } from "@/lib/utils";
import { DataTable } from "../../../../components/ui/data-table";
import DrawdownOverTimeChart from "./DrawdownOverTimeChart";
import DrawdownRecoveryMiniStats from "./DrawdownRecoveryMiniStats";
import EquityPeakPathChart from "./EquityPeakPathChart";

export type RiskMetricsDashboardSection =
  | "full"
  | "keyMetrics"
  | "drawdownChart"
  | "equityPeakPath";

interface RiskMetricsTabProps {
  filters?: TradeStatisticsFilters;
  comparisonFilters?: TradeStatisticsFilters;
  dashboardSections?: RiskMetricsDashboardSection;
  /** When set, skip internal risk-metrics fetch (dashboard shared fetch). */
  dataOverride?: RiskMetricsResponse | null;
  loadingOverride?: boolean;
  errorOverride?: string | null;
}

interface RiskMetricRow {
  metric: string;
  value: string;
}

const RISK_METRIC_TOOLTIPS: Record<string, string> = {
  "Maximum Drawdown":
    "The largest peak-to-trough loss over the period, measured in your account currency. Shows the worst historical drop from a high point before a new high was made.",
  "Max Drawdown %":
    "The largest peak-to-trough loss as a percentage of equity. Helps compare drawdowns across different account sizes.",
  "Sharpe Ratio":
    "Risk-adjusted return: average excess P&L divided by the standard deviation of P&L. Higher values mean better returns per unit of risk.",
  "Sortino Ratio":
    "Like Sharpe, but only penalizes downside volatility. Calculated as average excess P&L divided by the standard deviation of negative P&L.",
  "Risk/Reward Ratio":
    "Average loss size relative to average win size. A 1.5:1 ratio means your average win is 1.5× larger than your average loss.",
  "Calmar Ratio":
    "Annualized return divided by maximum drawdown. Rewards high returns with shallow drawdowns and penalizes deep drawdowns.",
  Volatility:
    "Standard deviation of P&L, expressed as a percentage. Higher volatility means more variability in returns (both up and down).",
};

const RiskMetricsTab: React.FC<RiskMetricsTabProps> = ({
  filters,
  comparisonFilters,
  dashboardSections = "full",
  dataOverride,
  loadingOverride,
  errorOverride,
}) => {
  const { formatMoney } = useDisplayCurrency();
  const cardBg = useColorModeValue("white", "#151517");
  const useExternalData = dataOverride !== undefined;
  const {
    data: hookData,
    loading: hookLoading,
    error: hookError,
    errorAction: hookErrorAction,
  } = useRiskMetrics(filters, { enabled: !useExternalData });
  const data = useExternalData ? dataOverride : hookData;
  const loading = useExternalData ? (loadingOverride ?? false) : hookLoading;
  const error = useExternalData ? (errorOverride ?? null) : hookError;
  const errorAction = useExternalData ? null : hookErrorAction;
  const { showWidgetError, widgetError, widgetAction } = useStatisticsWidgetErrorGate(error, errorAction);
  const { data: comparisonData, error: comparisonError } = useRiskMetrics(comparisonFilters, {
    enabled: Boolean(comparisonFilters),
  });
  const hasComparisonSelected = Boolean(
    comparisonFilters?.start_date && comparisonFilters?.end_date
  );

  const isFullDashboard = dashboardSections === "full";
  const isDashboardEmbed = !isFullDashboard;
  const showRecoveryMiniStats = isFullDashboard;
  const showKeyMetricsBlock = isFullDashboard || dashboardSections === "keyMetrics";
  const showDrawdownBlock = isFullDashboard || dashboardSections === "drawdownChart";
  const showEquityPeakBlock = isFullDashboard || dashboardSections === "equityPeakPath";

  const formatPercent = (value: number | null): string => {
    if (value === null) return "N/A";
    return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
  };

  const formatCurrencySafe = (value: number | null): string => {
    if (value === null) return "N/A";
    return formatMoney(value);
  };

  const riskMetricsTableData: RiskMetricRow[] = useMemo(() => {
    if (!data) return [];
    return [
      { metric: "Maximum Drawdown", value: formatCurrencySafe(data.max_drawdown) },
      { metric: "Max Drawdown %", value: formatPercent(data.max_drawdown_percent) },
      { metric: "Sharpe Ratio", value: data.sharpe_ratio !== null ? data.sharpe_ratio.toFixed(2) : "N/A" },
      { metric: "Sortino Ratio", value: data.sortino_ratio !== null ? data.sortino_ratio.toFixed(2) : "N/A" },
      { metric: "Risk/Reward Ratio", value: data.risk_reward_ratio !== null ? `${data.risk_reward_ratio.toFixed(2)}:1` : "N/A" },
      { metric: "Calmar Ratio", value: data.calmar_ratio !== null ? data.calmar_ratio.toFixed(2) : "N/A" },
      { metric: "Largest Losing Streak", value: data.largest_losing_streak.toString() },
      { metric: "Average Losing Trade", value: formatCurrencySafe(data.average_losing_trade) },
      { metric: "Volatility", value: formatPercent(data.volatility) },
    ];
  }, [data, formatMoney]);

  const comparisonTableData: RiskMetricRow[] = useMemo(() => {
    if (!hasComparisonSelected || !comparisonData) return [];
    return [
      { metric: "Maximum Drawdown", value: formatCurrencySafe(comparisonData.max_drawdown) },
      { metric: "Max Drawdown %", value: formatPercent(comparisonData.max_drawdown_percent) },
      { metric: "Sharpe Ratio", value: comparisonData.sharpe_ratio !== null ? comparisonData.sharpe_ratio.toFixed(2) : "N/A" },
      { metric: "Sortino Ratio", value: comparisonData.sortino_ratio !== null ? comparisonData.sortino_ratio.toFixed(2) : "N/A" },
      { metric: "Risk/Reward Ratio", value: comparisonData.risk_reward_ratio !== null ? `${comparisonData.risk_reward_ratio.toFixed(2)}:1` : "N/A" },
      { metric: "Calmar Ratio", value: comparisonData.calmar_ratio !== null ? comparisonData.calmar_ratio.toFixed(2) : "N/A" },
      { metric: "Largest Losing Streak", value: comparisonData.largest_losing_streak.toString() },
      { metric: "Average Losing Trade", value: formatCurrencySafe(comparisonData.average_losing_trade) },
      { metric: "Volatility", value: formatPercent(comparisonData.volatility) },
    ];
  }, [comparisonData, hasComparisonSelected, formatMoney]);

  const columns: ColumnDef<RiskMetricRow, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "metric",
        header: "Metric",
        enableSorting: false,
        cell: ({ row }) => {
          const metric = row.getValue("metric") as string;
          const description = RISK_METRIC_TOOLTIPS[metric];

          if (description) {
            return (
              <StatisticsDefinitionTooltip label={metric} description={description} />
            );
          }

          return (
            <span className={STATS_MUTED_TEXT_CLASS}>
              {metric}
            </span>
          );
        },
      },
      {
        accessorKey: "value",
        header: "Value",
        enableSorting: false,
        cell: ({ row }) => (
          <span className={STATS_MUTED_TEXT_CLASS}>
            {row.getValue("value")}
          </span>
        ),
      },
    ],
    [],
  );

  if (showWidgetError && widgetError) {
    return (
      <div className="rounded-md border border-border bg-card p-4">
        <StatisticsInlineError message={widgetError} action={widgetAction ?? undefined} />
      </div>
    );
  }

  const dashboardLoadingCardType =
    dashboardSections !== "full"
      ? riskMetricsSectionToCardType(dashboardSections)
      : null;
  if (
    isDashboardEmbed &&
    dashboardLoadingCardType &&
    (loading || isStatisticsFetchPending(data, error))
  ) {
    return (
      <DashboardWidgetLoadingCard
        cardType={dashboardLoadingCardType}
        variant={dashboardSections === "keyMetrics" ? "table" : "chart"}
        className="h-full min-h-0 w-full flex-1"
      />
    );
  }

  if (isStatisticsFetchPending(data, error)) {
    return <StatisticsTabLoadingState />;
  }

  if (hasComparisonSelected && isStatisticsFetchPending(comparisonData, comparisonError)) {
    return <StatisticsTabLoadingState />;
  }

  const selectedHasData = riskMetricsHasData(data);
  const comparisonHasData = riskMetricsHasData(comparisonData);

  if (!hasComparisonSelected && !selectedHasData) {
    return <StatisticsTabEmptyState />;
  }

  if (hasComparisonSelected && !selectedHasData && !comparisonHasData) {
    return <StatisticsTabEmptyState />;
  }

  const renderMetricsGrid = (
    metricsData: NonNullable<typeof data>,
    tableRows: RiskMetricRow[]
  ) => {
    const showPairRow = showKeyMetricsBlock || showDrawdownBlock;
    const isSingleDashboardEmbedChart =
      isDashboardEmbed && !showRecoveryMiniStats && !showPairRow && showEquityPeakBlock;
    const metricsGridColumns =
      showKeyMetricsBlock && showDrawdownBlock ? { base: 1, lg: 2 } : 1;
    const isPairedStatisticsLayout =
      isFullDashboard && showKeyMetricsBlock && showDrawdownBlock;
    const shouldMatchRowHeight = isDashboardEmbed || isPairedStatisticsLayout;

    return (
      <VStack
        spacing="24px"
        align="stretch"
        w="100%"
        {...(isSingleDashboardEmbedChart ? { h: "100%", flex: "1", minH: 0 } : {})}
      >
        {showRecoveryMiniStats ? (
          <DrawdownRecoveryMiniStats
            drawdownData={metricsData.drawdown_data}
            maxDrawdown={metricsData.max_drawdown}
          />
        ) : null}

        {showPairRow ? (
          <SimpleGrid
            columns={metricsGridColumns}
            spacing="24px"
            w="100%"
            {...(isDashboardEmbed ? { h: "100%", flex: "1" } : {})}
            alignItems={shouldMatchRowHeight ? "stretch" : "start"}
          >
            {showKeyMetricsBlock && (
              <Card
                data-statistics-shadcn-card
                className={cn(
                  statisticsShadcnCardClassName,
                  "p-4",
                  shouldMatchRowHeight && "h-full min-h-0"
                )}
              >
                <div className="mb-4">
                  <StatisticsCardHeadline>Key Risk Metrics</StatisticsCardHeadline>
                </div>
                <DataTable
                  columns={columns}
                  data={tableRows}
                  showPagination={false}
                  emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
                />
              </Card>
            )}
            {showDrawdownBlock && (
              <Box
                {...(shouldMatchRowHeight
                  ? { h: "100%", flex: "1", minH: 0, display: "flex", flexDirection: "column" }
                  : {})}
              >
                <DrawdownOverTimeChart
                  embedded={hasComparisonSelected}
                  fillHeight={shouldMatchRowHeight}
                  drawdownData={metricsData.drawdown_data}
                  maxDrawdown={metricsData.max_drawdown}
                  maxDrawdownPercent={metricsData.max_drawdown_percent}
                  error={error}
                  errorAction={errorAction}
                />
              </Box>
            )}
          </SimpleGrid>
        ) : null}

        {showEquityPeakBlock ? (
          <Box
            {...(isDashboardEmbed
              ? { h: "100%", flex: "1", minH: 0, display: "flex", flexDirection: "column" }
              : {})}
          >
            <EquityPeakPathChart
              embedded={hasComparisonSelected}
              fillHeight={isDashboardEmbed && dashboardSections === "equityPeakPath"}
              drawdownData={metricsData.drawdown_data}
              loading={loading}
              error={error}
              errorAction={errorAction}
            />
          </Box>
        ) : null}
      </VStack>
    );
  };

  const renderPeriodBlock = (
    periodData: typeof data,
    tableRows: RiskMetricRow[]
  ) => {
    if (!riskMetricsHasData(periodData)) {
      return <StatisticsTabEmptyState compact />;
    }
    return renderMetricsGrid(periodData!, tableRows);
  };

  return (
    <VStack
      spacing="24px"
      align="stretch"
      w="100%"
      {...(isDashboardEmbed ? { h: "100%", flex: "1", minH: 0 } : {})}
    >
      {hasComparisonSelected ? (
        <>
          <Box data-statistics-card-surface="" mb="24px" border="2px solid" borderColor="#38BBD1" borderRadius="lg" p={4} bg={cardBg}>
            {renderPeriodBlock(data, riskMetricsTableData)}
          </Box>
          <Box data-statistics-card-surface="" border="2px solid" borderColor="#F49113" borderRadius="lg" p={4} bg={cardBg}>
            {renderPeriodBlock(comparisonData, comparisonTableData)}
          </Box>
        </>
      ) : (
        renderMetricsGrid(data!, riskMetricsTableData)
      )}
    </VStack>
  );
};

export default RiskMetricsTab;
